<template>
  <mf-loading-dialog :loading="loading">
    <v-dialog persistent :value="value" :width="firstStep ? '700px' : '1000px'" @input="$emit('input', $event)">
      <v-card>
        <v-card-title class="table-card-title"> Importar arquivo </v-card-title>
        <v-card-text v-if="firstStep">
          <p>Selecione as unidades que serão atualizadas com o novo arquivo</p>

          <v-data-table
            v-model="selectedUnits"
            :items="units"
            :headers="[{ text: 'Lojas', value: 'name', sortable: false }]"
            item-key="id_erp"
            hide-default-footer
            disable-pagination
            show-select
          >
            <template v-slot:header.data-table-select="{ on, props }">
              <v-simple-checkbox color="primary" v-bind="props" v-on="on"></v-simple-checkbox>
            </template>

            <template v-slot:item="props">
              <tr class="cursor-pointer" @click="props.select(!props.isSelected)">
                <td>
                  <v-simple-checkbox color="primary" :value="props.isSelected" @input="props.select($event)"></v-simple-checkbox>
                </td>
                <td>{{ props.item.id_erp }} - {{ props.item.name }}</td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-text v-else class="import-file">
          <v-container fluid class="pa-0">
            <v-row>
              <v-col cols="5">
                <p class="text">Primeiramente selecione o arquivo .csv em seu computador.</p>

                <v-container fluid class="pa-0 import-file-block">
                  <v-row>
                    <v-col class="import-col" @click="activateInput">
                      <mf-icon fill color="verde-l20">cloud_upload</mf-icon>
                      <span class="ml-2 text import">{{ fileName ? fileName : 'Importar arquivo de produtos' }}</span>
                      <p class="text import-hint">Somente formato .csv</p>

                      <input id="dynamic-file-input" ref="file-input" class="hidden-input" type="file" @input="readFile($event)" />
                    </v-col>
                  </v-row>
                </v-container>

                <p class="text">Em seguida, selecione o que cada coluna representa na pré-visualização ao lado.</p>
                <p class="text mb-1">Valores que devem ser importados:</p>

                <ul>
                  <li v-for="field in fields" :key="field.value">{{ field.text }}</li>
                </ul>
              </v-col>
              <v-col cols="7" class="preview-col" align="center" justify="center">
                <v-container class="pa-0" fill-height>
                  <v-row justify="center">
                    <v-simple-table v-if="contentSample.length">
                      <tbody>
                        <tr>
                          <td v-for="(column, columnIndex) in contentSample[0]" :key="columnIndex" class="px-1 pb-2">
                            <v-select v-model="selectedColumns[columnIndex]" hide-details solo :items="fieldsWithDisabled" clearable></v-select>
                          </td>
                        </tr>
                        <tr v-for="(column, columnIndex) in contentSample" :key="columnIndex">
                          <td v-for="(line, lineIndex) in column" :key="lineIndex">
                            {{ line }}
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                    <span v-else>Aguardando arquivo em .csv</span>
                  </v-row>
                </v-container>
              </v-col>
            </v-row>
          </v-container>
          <mf-action-buttons
            v-if="firstStep"
            :primary-button="{
              text: 'Continuar',
              action: () => (firstStep = false),
              isVisible: true,
            }"
            :extra-button="{
              text: 'Fechar',
              action: cancel,
              isVisible: true,
            }"
          />
          <mf-action-buttons
            v-else
            :primary-button="{
              text: 'Finalizar',
              action: submit,
              isVisible: true,
              isDisabled: disableButton,
            }"
            :extra-button="{
              text: 'Voltar',
              action: back,
              isVisible: true,
              isDisabled: loading,
            }"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </mf-loading-dialog>
</template>

<script>
import CSV from 'csv-string'
import gql from 'graphql-tag'

const MUTATION_UPLOAD_ECOMMERCE_PRODUCTS_FILE = gql`
  mutation UploadEcommerceProductsFile($file: Upload!, $columns: EcommerceProductsFileColumns!, $units: [ID!]!) {
    uploadEcommerceProductsFile(file: $file, columns: $columns, units: $units) {
      uncategorized_products
      products_without_images
      altered_prices
      products_removed
      matched_products
      unmatched_products
    }
  }
`

export default {
  props: {
    value: Boolean,
    units: Array,
  },
  data: () => ({
    firstStep: true,
    selectedUnits: [],
    contentSample: [],
    selectedColumns: [],
    fileName: '',
    file: {},
    loading: false,
    fileTypes: [
      'text/comma-separated-values',
      'text/csv',
      'application/csv',
      'application/excel',
      'application/vnd.ms-excel',
      'application/vnd.msexcel',
      'text/anytext',
    ],
    fields: [
      {
        text: 'SKU (ID)',
        value: 'sku',
      },
      {
        text: 'EAN',
        value: 'ean',
      },
      {
        text: 'Preço',
        value: 'price',
      },
    ],
  }),
  computed: {
    fieldsWithDisabled() {
      return this.fields.map((obj) => {
        return { ...obj, disabled: Boolean(this.selectedColumns.includes(obj.value)) }
      })
    },
    disableButton() {
      return this.selectedColumns.filter((obj) => obj).length < 3
    },
  },
  methods: {
    cancel() {
      this.resetData()
      this.$emit('input', false)
    },
    activateInput() {
      this.$refs['file-input'].click()
      document.querySelector('#dynamic-file-input').value = '' // This enables secting the same file again
    },
    back() {
      this.firstStep = true
      this.file = {}
      this.contentSample = []
      this.fileName = ''
      document.querySelector('#dynamic-file-input').value = '' // This enables secting the same file again
    },
    readFile(event) {
      if (event.target.files.length === 0) return

      if (Boolean(event.target.files[0].type) && !this.fileTypes.includes(event.target.files[0].type)) {
        console.log('Error loading ', event.target.files[0].type, 'file')
        this.$snackbar({ message: 'Permitido apenas arquivos em .csv', snackbarColor: 'error' })
        return
      }

      this.selectedColumns = []
      this.file = event.target.files[0]
      this.fileName = this.file.name

      let reader = new FileReader()
      reader.onload = () => {
        this.contentSample = CSV.parse(reader.result).slice(0, 6)
      }

      reader.readAsText(this.file)
    },
    resetData() {
      this.firstStep = true
      this.selectedUnits = []
      this.contentSample = []
      this.selectedColumns = []
      this.fileName = ''
      this.file = {}
    },
    async submit() {
      this.loading = true
      try {
        const columns = {}
        this.selectedColumns.forEach((column, index) => {
          if (column) columns[column] = index
        })

        const {
          data: { uploadEcommerceProductsFile },
        } = await this.$apollo.mutate({
          mutation: MUTATION_UPLOAD_ECOMMERCE_PRODUCTS_FILE,
          variables: {
            file: this.file,
            columns: columns,
            units: this.selectedUnits.map((unit) => unit.id_erp),
          },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        this.$emit('input', false)
        this.$emit('finished', uploadEcommerceProductsFile)
        this.resetData()
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
.import-file {
  .import-col {
    cursor: pointer;
    user-select: none;
  }
  .hidden-input {
    display: none;
  }
  .text {
    opacity: 0.87;
    max-width: 300px;
    &.import {
      color: $verde-l20;
    }
    &.import-hint {
      font-size: 12px;
      opacity: 0.54;
    }
  }
  .category-box {
    .select-label {
      opacity: 0.87;
    }
    .disabled-opacity {
      opacity: 0.54;
    }
  }
  .preview-col {
    background-color: $cinza-l90;
    border-radius: 4px;
    border: 1px solid $cinza-d10;
    padding: 2px;
  }

  .v-data-table {
    max-width: 545px;
    user-select: none;
    td {
      border: 1px solid transparent;
      min-width: 200px;
    }
    tr {
      &:hover {
        background-color: $branco !important;
      }
    }
  }
}
</style>
